import React, { useContext, useEffect, useState, useCallback } from 'react';
import { FileText, Plus, User, Calendar, Edit, Trash } from 'lucide-react';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { NotesApi } from 'api-client/apis';
import type { Note } from 'api-client/models';
import { getApiConfiguration } from '@/api/utils';
import { EditeNoteDialog } from '@/components/ui/EditeNoteDialog';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from 'sonner';

interface AssetNotesProps {
  objectId: string;
  objectType: string;
}

export const AssetNotes: React.FC<AssetNotesProps> = ({ objectId, objectType }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(0);
  const { currentTeam } = useTeams();
  const { token, user } = useContext(AuthContext);
  const client = new NotesApi(getApiConfiguration(token));
  const confirmDialog = useConfirmDialog();

  // Fetch notes based on object ID and type
  const fetchNotes = useCallback(async () => {
    if (!currentTeam?.slug || !token) return;
    
    try {
      setLoadingNotes(true);
      const response = await client.notesList({
        teamSlug: currentTeam.slug,
        objectType,
        objectId
      });
      
      if (response.results) {
        setNotes(response.results);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, [client, currentTeam?.slug, objectId, objectType, token]);

  const createNote = async () => {
    if (!currentTeam?.slug || !token || !selectedNote?.content) return;
    
    try {
      setLoadingNotes(true);
      const response = await client.notesCreate({
        teamSlug: currentTeam.slug,
        note: {
          contentType: +objectType,
          createdByName: user?.getDisplayName || "",
          createdBy: user?.id || 0,
          contentTypeName: objectType,
          createdByAvatar: user?.avatarUrl || "",
          objectType,
          objectId: +objectId,
          content: selectedNote.content,
          createdAt: new Date(),
          updatedAt: new Date()
        }
      });
      
      if (response) {
        setNotes(prev => [...prev, response]);
        setSelectedNote(null);
      }
    } catch (error) {
      console.error('Error creating note:', error);
    } finally {
      setLoadingNotes(false);
      toast.success('Note created successfully');
    }
  };

  const editNote = async (id: string, note: Note) => {
    console.log(!currentTeam?.slug, !id, !note, note)
    if (!currentTeam?.slug || !id || !note) return;
    
    try {
      setLoadingNotes(true);
      const response = await client.notesUpdate({
        id,
        teamSlug: currentTeam.slug,
        note: {...note, objectType}
      });
      
      if (response) {
        setNotes(prev => prev.map(note => 
          note.id === response.id ? response : note
        ));
      }
    } catch (error) {
      console.log('Error updating note:', error);
    } finally {
      setLoadingNotes(false);
      toast.success('Note updated successfully');
    }
  };

  const deleteNote = async (id: string) => {
    if (!currentTeam?.slug || !id) return;
    
    try {
      setLoadingNotes(true);
      await client.notesDestroy({
        id,
        teamSlug: currentTeam.slug
      });
      setNotes(prev => prev.filter(note => note.id?.toString() !== id));
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setLoadingNotes(false);
      toast.success('Note deleted successfully');
    }
  };

  useEffect(() => {
    if (loadingNotes) {
    }
    fetchNotes();
  }, []);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {notes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((note) => (
          <div
            key={note.id}
            className="group p-4 bg-gray-800/30 border border-gray-800 rounded-lg"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                {note.createdByAvatar ? (
                  <img
                    src={`http://127.0.0.1:8000${note.createdByAvatar}`}
                    alt={note.createdByName}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-700">
                    <User className="h-4 w-4 text-gray-400" />
                  </div>
                )}
                <div className="break-words">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-white">{note.createdByName}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(note.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-300 break-words" style={{ wordBreak: 'break-word' }}>
                    {note.content}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {user?.id === note.createdBy && (
                  <>
                    <button
                      className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-gray-800/50 transition-colors"
                      onClick={() => {
                        setIsDialogOpen(note.id)
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {isDialogOpen === note.id && (
                      <EditeNoteDialog key={note?.id} isDialogOpen={isDialogOpen} setIsDialogOpen={setIsDialogOpen} note={note} editNote={editNote} />
                    )}
                    <button
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-800/50 transition-colors"
                      onClick={async () => {
                        const confirmed = await confirmDialog.confirm({
                          title: `Delete note ${note.id}?`,
                          description: 'This action is permanent.',
                          confirmLabel: 'Delete',
                          variant: 'destructive'
                        });
                        if (confirmed) {
                          await deleteNote(note.id?.toString());
                        }
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Notes</h2>
          </div>
          <button
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={createNote}
            disabled={!selectedNote?.content}
          >
            <Plus className="h-4 w-4" />
            <span>Add Note</span>
          </button>
        </div>

        <textarea
          placeholder="Add a note..."
          className="w-full h-32 px-4 py-3 bg-gray-800/30 border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
          value={selectedNote?.content || ''}
          onChange={(e) => setSelectedNote({ content: e.target.value } as Note)}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

export default AssetNotes;